import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart3, TrendingUp, Users, ArrowUpRight, ArrowDownRight, Wallet, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Statistics() {
  const [portfolios, setPortfolios] = useState([]);
  const [portfolioStats, setPortfolioStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedPortfolio, setExpandedPortfolio] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Obtener todas las carteras del usuario
      const portfoliosResponse = await axios.get(`${API}/carteras`);
      const portfoliosList = portfoliosResponse.data;
      setPortfolios(portfoliosList);
      
      // Obtener estadísticas para cada cartera
      const statsPromises = portfoliosList.map(portfolio => 
        axios.get(`${API}/statistics/by-holder?portfolio_id=${portfolio.id}`)
          .then(response => ({ portfolioId: portfolio.id, data: response.data }))
          .catch(error => {
            console.error(`Error loading stats for portfolio ${portfolio.id}:`, error);
            return { portfolioId: portfolio.id, data: null };
          })
      );
      
      const statsResults = await Promise.all(statsPromises);
      
      // Convertir array a objeto con portfolioId como key
      const statsMap = {};
      statsResults.forEach(result => {
        statsMap[result.portfolioId] = result.data;
      });
      
      setPortfolioStats(statsMap);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calcular totales consolidados de todas las carteras
  const calculateConsolidatedTotals = () => {
    let totalReceipts = 0;
    let totalAmount = 0;
    let allHolders = new Set();

    portfolios.forEach(portfolio => {
      const stats = portfolioStats[portfolio.id];
      if (stats) {
        totalReceipts += stats.total_receipts || 0;
        totalAmount += stats.total_amount || 0;
        
        // Agregar holders únicos
        if (stats.by_destination) {
          stats.by_destination.forEach(item => {
            if (item.holder) allHolders.add(item.holder);
          });
        }
        if (stats.by_origin) {
          stats.by_origin.forEach(item => {
            if (item.holder) allHolders.add(item.holder);
          });
        }
      }
    });

    return {
      totalReceipts,
      totalAmount,
      uniqueHolders: allHolders.size,
      totalPortfolios: portfolios.length
    };
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando estadísticas...</p>
          </div>
        </div>
      </div>
    );
  }

  if (portfolios.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="mb-8 bg-gradient-to-br from-indigo-100 to-purple-100 border-indigo-200">
          <CardContent className="py-12 text-center">
            <BarChart3 className="w-16 h-16 text-indigo-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No hay carteras</h3>
            <p className="text-gray-600 mb-6">
              Crea tu primera cartera para comenzar a ver estadísticas.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const consolidated = calculateConsolidatedTotals();

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Estadísticas y Análisis Consolidadas
        </h1>
        <p className="text-gray-600">Vista general de todas tus carteras con análisis detallado</p>
      </div>

      {/* Consolidated Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              Total Carteras
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-indigo-600">{consolidated.totalPortfolios}</p>
            <p className="text-xs text-gray-500 mt-1">Carteras activas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Total Comprobantes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-gray-900">{consolidated.totalReceipts}</p>
            <p className="text-xs text-gray-500 mt-1">En todas las carteras</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Monto Total Consolidado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">
              ${consolidated.totalAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-gray-500 mt-1">Suma de todas las carteras</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Titulares Únicos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-purple-600">
              {consolidated.uniqueHolders}
            </p>
            <p className="text-xs text-gray-500 mt-1">Todos los titulares</p>
          </CardContent>
        </Card>
      </div>

      {/* Detalle por Cartera */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Detalle por Cartera</h2>
        <div className="space-y-4">
          {portfolios.map(portfolio => {
            const stats = portfolioStats[portfolio.id];
            const isExpanded = expandedPortfolio === portfolio.id;
            
            return (
              <Card key={portfolio.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl flex items-center gap-2">
                        <Wallet className="w-5 h-5" style={{ color: portfolio.color }} />
                        {portfolio.name}
                      </CardTitle>
                      {portfolio.description && (
                        <CardDescription className="mt-1">{portfolio.description}</CardDescription>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-2xl font-bold text-green-600">
                        ${(stats?.total_amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-gray-500">{stats?.total_receipts || 0} comprobantes</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedPortfolio(isExpanded ? null : portfolio.id)}
                    className="mb-4"
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="w-4 h-4 mr-2" />
                        Ocultar detalles
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-2" />
                        Ver análisis detallado
                      </>
                    )}
                  </Button>

                  {isExpanded && stats && (
                    <div className="space-y-6 mt-4">
                      {/* Stats Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600 mb-1">Total Comprobantes</p>
                          <p className="text-2xl font-bold text-gray-900">{stats.total_receipts || 0}</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600 mb-1">Monto Total</p>
                          <p className="text-2xl font-bold text-green-600">
                            ${(stats.total_amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600 mb-1">Titulares Únicos</p>
                          <p className="text-2xl font-bold text-purple-600">{stats.unique_holders || 0}</p>
                        </div>
                      </div>

                      {/* By Destination */}
                      {stats.by_destination && stats.by_destination.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <ArrowUpRight className="w-4 h-4 text-green-600" />
                            Análisis por Destinatario
                          </h4>
                          <div className="space-y-2">
                            {stats.by_destination.map((item, index) => (
                              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">{item.holder || 'Sin especificar'}</p>
                                  <p className="text-xs text-gray-500">{item.count} comprobante{item.count !== 1 ? 's' : ''}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-green-600">
                                    ${item.total_amount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Promedio: ${(item.total_amount / item.count).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* By Origin */}
                      {stats.by_origin && stats.by_origin.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <ArrowDownRight className="w-4 h-4 text-blue-600" />
                            Análisis por Origen
                          </h4>
                          <div className="space-y-2">
                            {stats.by_origin.map((item, index) => (
                              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">{item.holder || 'Sin especificar'}</p>
                                  <p className="text-xs text-gray-500">{item.count} comprobante{item.count !== 1 ? 's' : ''}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-blue-600">
                                    ${item.total_amount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Promedio: ${(item.total_amount / item.count).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

    </div>
  );
}
